<template>
  <CustomModal ref="modal" :title="supplier ? supplier.name : 'Fournisseur'" class="supplier-detail-modal">
    <div class="container">
      <p v-if="identity" class="identity">{{ identity }}</p>

      <div class="tiles">
        <div class="tile">
          <span class="tile-label">Achats</span>
          <span class="tile-value">{{ totals.count }}</span>
        </div>
        <div class="tile">
          <span class="tile-label">Total TTC</span>
          <span class="tile-value">{{ formatAmount(totals.ttcAmount) }}</span>
        </div>
        <div class="tile">
          <span class="tile-label">Décaissé</span>
          <span class="tile-value">{{ formatAmount(totals.paidAmount) }}</span>
        </div>
        <div class="tile" :class="{overdue: totals.overdueAmount > 0}">
          <span class="tile-label">Reste à payer</span>
          <span class="tile-value">{{ formatAmount(totals.remainingAmount) }}</span>
        </div>
      </div>

      <div class="section">
        <p class="section-label">Achats</p>
        <table v-if="purchases.length > 0" class="mini-table">
          <tbody>
            <tr v-for="purchase in purchases" :key="purchase.id">
              <td>{{ formatDate(purchase.invoiceDate) }}</td>
              <td class="fill">{{ purchase.reference ? purchase.label + ' (' + purchase.reference + ')' : purchase.label }}</td>
              <td class="amount">{{ formatSignedAmount(purchase, purchase.ttcAmount) }}</td>
              <td><StatusBadge :status="purchase.status" :status-enum="statusEnumOf(purchase)" /></td>
            </tr>
          </tbody>
        </table>
        <p v-else class="hint">Aucun achat enregistré</p>
      </div>

      <div class="section">
        <p class="section-label">Décaissements</p>
        <table v-if="disbursements.length > 0" class="mini-table">
          <tbody>
            <tr v-for="disbursement in disbursements" :key="disbursement.id">
              <td>{{ formatDate(disbursement.date) }}</td>
              <td class="fill">{{ disbursement.purchaseLabel }}</td>
              <td>{{ labelOf(DISBURSEMENT_PAYMENT_TYPE_ENUM, disbursement.paymentType) }}</td>
              <td class="amount">{{ formatAmount(disbursement.isRefund ? -disbursement.amount : disbursement.amount) }}</td>
            </tr>
          </tbody>
        </table>
        <p v-else class="hint">Aucun décaissement enregistré</p>
      </div>

      <div class="button-container">
        <CustomButton class="button" @click="edit" :params=buttons.edit />
        <CustomButton class="button" :is-secondary="true" @click="closeModal()" :params=buttons.close />
      </div>
    </div>
  </CustomModal>
  <SupplierModal ref="supplierModal" :callback="onSupplierEdited" />
</template>

<script>
import CustomButton from "@/components/forms/customs/customButton/CustomButton.vue";
import CustomModal from "@/components/modals/CustomModal.vue";
import StatusBadge from "@/components/tables/commons/StatusBadge.vue";
import {formatIdentifier, SIREN_MASK} from "@/utils";
import {PurchaseController} from "@/purchase/controllers/PurchaseController";
import {DisbursementController} from "@/purchase/controllers/DisbursementController";
import SupplierModal from "@/purchase/components/modals/SupplierModal.vue";
import {
  DISBURSEMENT_PAYMENT_TYPE_ENUM,
  PURCHASE_STATUS_ENUM,
  formatAmount,
  formatDate,
  documentSign,
  formatSignedAmount,
  idOf,
  labelOf,
  round2,
  statusEnumOf
} from "@/purchase/utils";

// Fiche d'un fournisseur : ses totaux, ses achats et ses décaissements. À ouvrir avec openModal(fournisseur).
// Les avoirs (et leurs remboursements) se retranchent des totaux.
// callback est appelé quand le fournisseur est modifié depuis la fiche.
export default {
  name: 'SupplierDetailModal',
  props: ['callback'],
  components: {SupplierModal, StatusBadge, CustomModal, CustomButton},
  data() {
    return {
      supplier: undefined,
      purchases: [],
      disbursements: [],
      buttons: {
        edit: {text: "Modifier"},
        close: {text: "Fermer"},
      },
      PURCHASE_STATUS_ENUM,
      DISBURSEMENT_PAYMENT_TYPE_ENUM
    };
  },
  computed: {
    identity() {
      if (!this.supplier) return '';
      return [
        this.supplier.siren ? 'SIREN ' + formatIdentifier(this.supplier.siren, SIREN_MASK) : undefined,
        this.supplier.vatNumber,
        this.supplier.email,
        this.supplier.phone
      ].filter(Boolean).join(' · ');
    },
    // Les achats à vérifier ne sont pas encore des dépenses confirmées : ils ne comptent pas dans les totaux
    totals() {
      const confirmed = this.purchases.filter(purchase => purchase.status !== PURCHASE_STATUS_ENUM.TO_REVIEW.value);
      const sum = field => round2(confirmed.reduce((total, purchase) => total + documentSign(purchase) * Math.round((purchase[field] ?? 0) * 100), 0) / 100);
      return {
        count: confirmed.length,
        ttcAmount: sum('ttcAmount'),
        paidAmount: sum('paidAmount'),
        remainingAmount: sum('remainingAmount'),
        overdueAmount: round2(confirmed.filter(purchase => purchase.isOverdue).reduce((total, purchase) => total + purchase.remainingAmount, 0))
      };
    }
  },
  methods: {
    formatAmount,
    formatDate,
    formatSignedAmount,
    labelOf,
    statusEnumOf,
    async openModal(supplier) {
      this.supplier = supplier;
      this.purchases = [];
      this.disbursements = [];
      this.$refs.modal.openModal();
      await this.loadData();
    },
    async loadData() {
      const id = idOf(this.supplier);
      const [purchases, disbursements] = await Promise.all([
        PurchaseController.getBySupplier(id),
        DisbursementController.getBySupplier(id)
      ]);
      this.purchases = purchases && !purchases.errors ? purchases.sort((a, b) => new Date(b.invoiceDate) - new Date(a.invoiceDate)) : [];
      this.disbursements = disbursements && !disbursements.errors ? disbursements.sort((a, b) => new Date(b.date) - new Date(a.date)) : [];
    },
    closeModal(event = false) {
      this.$refs.modal?.closeModal(event);
    },
    edit() {
      this.$refs.supplierModal.openModal(this.supplier);
    },
    // Le fournisseur a changé : la fiche et le tableau parent se mettent à jour
    onSupplierEdited() {
      this.closeModal(true);
      if (this.callback) this.callback();
    }
  }
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';

.container
  display flex
  flex-direction column
  gap 10px

  .identity
    margin 0
    color #6b7280
    font-size 0.9em

  .tiles
    display grid
    grid-template-columns repeat(4, 1fr)
    gap 10px

    .tile
      display flex
      flex-direction column
      padding 8px 10px
      border-radius 8px
      background #f6f7f8

      .tile-label
        font-size 0.75em
        color #6b7280

      .tile-value
        font-size 1.05em
        font-weight 600

      &.overdue .tile-value
        color #dc2626

  .section
    padding-top 10px
    border-top 1px solid #eef0f2

    .section-label
      margin 0 0 6px
      font-size 9.5px
      font-weight 700
      letter-spacing 0.05em
      text-transform uppercase
      color #b4b8bd

    .hint
      margin 0
      font-size 0.85em
      color #9ca3af

    .mini-table
      display block
      max-height 170px
      overflow-y auto
      border-collapse collapse
      font-size 0.9em

      tbody
        display table
        width 100%

      td
        padding 3px 8px
        white-space nowrap

        &.fill
          width 100%
          max-width 0
          overflow hidden
          text-overflow ellipsis

        &.amount
          text-align right
          font-weight 600

  // Le bouton principal est en premier dans le DOM, l'ordre visuel est inversé : [Fermer] [Modifier]
  .button-container
    display flex
    flex-direction row-reverse
    justify-content flex-start
    gap 10px
    margin-top 4px

    .button
      min-width 110px

@media (max-width 600px)
  .container .tiles
    grid-template-columns 1fr 1fr
</style>

<style lang="stylus">
.supplier-detail-modal
  > .modal
    width 640px !important
    max-width 100%
    height auto !important
    max-height 92vh
    overflow-y auto
</style>
